/**
 * Helper compartilhado para datas no formato YYYY-MM-DD (date-only).
 *
 * Corrige o bug onde valores como "2026-08-23" são interpretados por
 * `new Date("2026-08-23")` como UTC midnight e, em America/Sao_Paulo
 * (UTC-3), exibidos como o dia anterior (22/08/2026).
 *
 * A abordagem valida o padrão YYYY-MM-DD, separa ano/mês/dia
 * numericamente e formata como data civil brasileira — sem conversão
 * UTC/local e sem o paliativo "T12:00".
 *
 * Preserve sempre o valor ISO original nos dados, sitemap e campos
 * machine-readable/schema (datePublished, dateModified). Use estas
 * funções apenas para exibição humana.
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const MESES_LONGOS = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];

const MESES_CURTOS = [
  'jan', 'fev', 'mar', 'abr', 'mai', 'jun',
  'jul', 'ago', 'set', 'out', 'nov', 'dez',
];

const DIAS_NO_MES = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isAnoBissexto(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function diasNoMes(year, month) {
  if (month === 2 && isAnoBissexto(year)) return 29;
  return DIAS_NO_MES[month - 1];
}

/**
 * Valida e decompõe uma data YYYY-MM-DD em { year, month, day } numéricos.
 * Retorna null para formato inválido ou data inexistente (ex.: 2026-02-30).
 */
export function parseDateOnly(isoDate) {
  if (typeof isoDate !== 'string') return null;
  const match = isoDate.trim().match(DATE_ONLY_RE);
  if (!match) return null;
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);
  if (month < 1 || month > 12) return null;
  if (day < 1 || day > diasNoMes(year, month)) return null;
  return { year, month, day };
}

/**
 * Formata YYYY-MM-DD como dd/mm/aaaa (data civil brasileira).
 * Sem conversão UTC/local. Retorna a string original se inválida,
 * ou string vazia se falsy.
 */
export function formatDateOnly(isoDate) {
  if (!isoDate) return '';
  const parts = parseDateOnly(isoDate);
  if (!parts) return String(isoDate);
  const dd = String(parts.day).padStart(2, '0');
  const mm = String(parts.month).padStart(2, '0');
  return `${dd}/${mm}/${parts.year}`;
}

/**
 * Formata YYYY-MM-DD por extenso: "d de mês de aaaa".
 * Ex.: "2026-08-23" -> "23 de agosto de 2026".
 */
export function formatDateOnlyLong(isoDate) {
  if (!isoDate) return '';
  const parts = parseDateOnly(isoDate);
  if (!parts) return String(isoDate);
  return `${parts.day} de ${MESES_LONGOS[parts.month - 1]} de ${parts.year}`;
}

/**
 * Formata YYYY-MM-DD de forma curta.
 * style 'abbr' (padrão): "23 ago"
 * style 'numeric': "23/08"
 */
export function formatDateOnlyShort(isoDate, style = 'abbr') {
  if (!isoDate) return '';
  const parts = parseDateOnly(isoDate);
  if (!parts) return String(isoDate);
  const dd = String(parts.day).padStart(2, '0');
  if (style === 'numeric') {
    const mm = String(parts.month).padStart(2, '0');
    return `${dd}/${mm}`;
  }
  return `${dd} ${MESES_CURTOS[parts.month - 1]}`;
}